// Routes every external link to the in-app browser on native, so tapping a
// link never app-switches the user out of Rubra.
//
// This is a single delegated listener rather than per-call-site changes: the
// app has ~40 `target="_blank"` anchors (bookmark sources, account nav, the
// legal pages, and every link inside sanitized note HTML) and any new one
// should get this behaviour for free.
//
// The listener runs in the CAPTURE phase so a stopPropagation() anywhere up
// the tree cannot silently drop interception. Running first is safe for
// <router-link>: vue-router's guardEvent() bails when e.defaultPrevented is
// already set, so it will not navigate a second time after we handle a click.

import { Capacitor } from '@capacitor/core'

import { setupRegistry } from '.'
import { openExternalUrl } from '@/utils/external-link'
import { LINK_TARGET, classifyLinkHref } from '@/utils/link-target'
import { LINK_SELECTOR, DOWNLOAD_ATTRIBUTE } from '@/constants/inAppBrowser'

const PLUGIN_KEY = 'in-app-browser'
const USE_CAPTURE = true

// Imported lazily: this module is eagerly loaded by the plugin glob, and the
// router pulls in the store and i18n at boot.
async function navigateInApp(path) {
  const { default: router } = await import('@/plugins/router')
  // This app registers no catch-all route, so an unknown path matches nothing.
  if (!router.resolve(path).matched.length) return false
  router.push(path)
  return true
}

function onDocumentClick(event) {
  if (event.defaultPrevented) return

  const anchor = event.target?.closest?.(LINK_SELECTOR)
  if (!anchor || anchor.hasAttribute(DOWNLOAD_ATTRIBUTE)) return

  const { target, href, path } = classifyLinkHref(anchor.getAttribute('href'), window.location.href)
  if (target === LINK_TARGET.IGNORE) return

  event.preventDefault()

  if (target === LINK_TARGET.EXTERNAL) {
    openExternalUrl(href)
    return
  }

  // Same origin: route in-app instead of letting the WebView cold-reload the
  // whole SPA. If the path is not one of ours, treat it as an external link
  // rather than dumping the user on a blank in-app route.
  navigateInApp(path).then(handled => {
    if (!handled) openExternalUrl(href)
  })
}

export const setupInAppBrowser = () => {
  if (setupRegistry.plugins.has(PLUGIN_KEY)) return
  // Web and extension builds keep their existing new-tab behaviour.
  if (!Capacitor.isNativePlatform()) return

  document.addEventListener('click', onDocumentClick, USE_CAPTURE)
  setupRegistry.plugins.add(PLUGIN_KEY)
}

export default true
